import { BILLING_CYCLES, PRODUCTS, ADDON_PRODUCTS, DEDICATED_KINDS, DEFAULT_SELECTION } from './config.js';
import { classifyNicknameLoose, parseNickname, PRICE_VARIANTS, productFromDisplayName } from './priceCatalog.js';
import { listActiveSubscriptions } from './subscriptionStatuses.js';
import {
  copierQuantitiesFromSubscriptions,
  dedicatedEnvLinesOf,
  findLineItemForProduct,
  isDedicatedEnv,
  isSignalManager,
  isEaHosting,
  subscriptionLineItems
} from './subscriptionLines.js';

/**
 * Single pass over subs → parallel arrays for DE rendering.
 * Index N in each array describes the same environment.
 *
 * Handles both:
 *   • Bundle subs — DE lives as one or more `line_items[]` entries inside a
 *     non-DE primary sub (post-multi-item-DE world). Each line yields its own
 *     env with `subItemId` set.
 *   • Legacy solo DE subs — entire sub IS the env. `subItemId` left null;
 *     fallback to `sub.dedicatedNickName` for the label.
 */
/** Classify an owned DE line/sub as CFD or Futures from its PRICE nickname. */
function dedicatedKindFromNickname(priceNickname) {
  const product = classifyNicknameLoose(priceNickname)?.product;
  return product === ADDON_PRODUCTS.dedicatedFutures ? DEDICATED_KINDS.futures : DEDICATED_KINDS.cfd;
}

function dedicatedEnvArrays(subscriptions, catalog = null) {
  const accounts = [];
  const subIds = [];
  const subItemIds = [];
  const nicknames = [];
  const subLineCounts = [];
  const kinds = [];

  for (const sub of subscriptions) {
    const deLines = dedicatedEnvLinesOf(sub, catalog);
    // Total lines on the parent sub (DE + non-DE). Cancel UX uses this to pick
    // RemoveSubscriptionItem (>1) vs CancelSubscription (sole line — BE rejects
    // removing the last item).
    const subLineCount = subscriptionLineItems(sub).length || 1;

    if (deLines.length === 0) {
      // No proto line_items but sub flagged DE by nickname/name (pure legacy fallback).
      if (isDedicatedEnv(sub) && (sub.lineItems?.length ?? 0) === 0) {
        accounts.push(Math.max(1, Number(sub.quantity) || 1));
        subIds.push(sub.id || sub.ID || null);
        subItemIds.push(null);
        nicknames.push(sub.dedicatedNickName || null);
        subLineCounts.push(1);
        kinds.push(dedicatedKindFromNickname(sub.priceNickname));
      }
      continue;
    }

    for (const line of deLines) {
      // Each DE line item is one env; `line.quantity` = account count inside
      // that env (NOT number of envs). Bundles get N line items each w/ own
      // account limit; legacy solo DE subs surface as a single DE line.
      accounts.push(Math.max(1, Number(line.quantity) || 1));
      subIds.push(sub.id || sub.ID || null);
      subItemIds.push(line.subscriptionItemId || null);
      // Per-line nick (bundle case) preferred. Fall back to sub-level only when this
      // is the sole DE line on the sub — matches BE: sub-level nick populated only
      // for single-DE subs (legacy compat).
      const fallback = deLines.length === 1 ? sub.dedicatedNickName || null : null;
      nicknames.push(line.dedicatedNickName || fallback);
      subLineCounts.push(subLineCount);
      // Kind from the PRICE nickname, not the display nickname above.
      kinds.push(dedicatedKindFromNickname(line.priceNickname || sub.priceNickname));
    }
  }
  return { accounts, subIds, subItemIds, nicknames, subLineCounts, kinds };
}

export function subscriptionBillingCycle(sub) {
  const nickMeta = parseNickname(sub.priceNickname);
  if (nickMeta?.cycle) return nickMeta.cycle;
  return intervalToBillingCycle(sub.interval, sub.intervalCount);
}

/**
 * Find the active analyzer sub on a given billing cycle, if any.
 */
export function analyzerSubscriptionForCycle(subscriptions, cycle) {
  const active = listActiveSubscriptions(subscriptions);
  return active.find((s) => productFromSubscription(s) === ADDON_PRODUCTS.analyzer && subscriptionBillingCycle(s) === cycle) || null;
}

/**
 * Find the (sub, line) owning analyzer on a cycle, plus line count of that sub.
 * Handles both single-item legacy subs AND bundle subs where analyzer is one of N lines.
 * Caller uses lineCount to pick RemoveSubscriptionItem (>1 line) vs CancelSubscription (sole line).
 *
 * @returns {{ subscription: object, subscriptionItemId: string|null, lineCount: number } | null}
 */
export function analyzerOwnerForCycle(subscriptions, cycle, catalog) {
  const active = listActiveSubscriptions(subscriptions);
  for (const sub of active) {
    if (subscriptionBillingCycle(sub) !== cycle) continue;
    const line = catalog ? findLineItemForProduct(sub, ADDON_PRODUCTS.analyzer, catalog) : null;
    if (line) {
      const lineCount = subscriptionLineItems(sub).length || 1;
      return {
        subscription: sub,
        subscriptionItemId: line.subscriptionItemId || null,
        lineCount
      };
    }
    if (productFromSubscription(sub) === ADDON_PRODUCTS.analyzer) {
      return {
        subscription: sub,
        subscriptionItemId: null,
        lineCount: 1
      };
    }
  }
  return null;
}

/**
 * Find any active analyzer ownership across all billing cycles.
 *
 * Analyzer is globally unique per user — one purchase covers all accounts on
 * any cycle. This locator powers cross-cycle UI gating and pre-flight
 * uniqueness checks before AddItem / CreateSub.
 *
 * @param {object[]} subscriptions
 * @param {object|null} [catalog] price catalog; without it only whole-sub
 *   ownership is detected, not a line inside a bundle sub
 * @returns {{ subscription: Record<string, any>, subscriptionItemId: string|null, cycle: string, currentPeriodEnd: number|null, lineCount: number } | null}
 */
/**
 * Find an add-on's active ownership across all billing cycles.
 *
 * Analyzer and MCP are each globally unique per user — one subscription
 * entitles them on any cycle — and the per-product lookups were byte-identical
 * apart from the product key, so they share this. Powers cross-cycle uniqueness
 * checks: a different-cadence checkout becomes a cadence change, not a
 * duplicate AddItem / CreateSubscription.
 *
 * @param {object[]} subscriptions
 * @param {string} product ADDON_PRODUCTS key
 * @param {object|null} [catalog] price catalog; without it only whole-sub
 *   ownership is detected, not a line inside a bundle sub
 * @returns {{ subscription: Record<string, any>, subscriptionItemId: string|null, cycle: string, currentPeriodEnd: number|null, lineCount: number } | null}
 */
export function findActiveAddon(subscriptions, product, catalog = null) {
  const active = listActiveSubscriptions(subscriptions);
  for (const sub of active) {
    if (isDedicatedEnv(sub)) continue;
    const line = catalog ? findLineItemForProduct(sub, product, catalog) : null;
    if (line) {
      return {
        subscription: sub,
        subscriptionItemId: line.subscriptionItemId || null,
        cycle: subscriptionBillingCycle(sub),
        currentPeriodEnd: sub.currentPeriodEnd ?? null,
        lineCount: subscriptionLineItems(sub).length || 1
      };
    }
    if (productFromSubscription(sub) === product) {
      return {
        subscription: sub,
        subscriptionItemId: null,
        cycle: subscriptionBillingCycle(sub),
        currentPeriodEnd: sub.currentPeriodEnd ?? null,
        lineCount: 1
      };
    }
  }
  return null;
}

/**
 * Analyzer ownership on any cycle. See findActiveAddon.
 * @param {object[]} subscriptions
 * @param {object|null} [catalog]
 */
export function findActiveAnalyzer(subscriptions, catalog = null) {
  return findActiveAddon(subscriptions, ADDON_PRODUCTS.analyzer, catalog);
}

/**
 * True if the user owns analyzer on any cycle.
 * @param {object[]} subscriptions
 * @param {object|null} [catalog]
 */
export function hasActiveAnalyzer(subscriptions, catalog = null) {
  return findActiveAnalyzer(subscriptions, catalog) !== null;
}

/**
 * MCP (AI Trading) ownership on any cycle. See findActiveAddon.
 * @param {object[]} subscriptions
 * @param {object|null} [catalog]
 */
export function findActiveMcp(subscriptions, catalog = null) {
  return findActiveAddon(subscriptions, ADDON_PRODUCTS.mcp, catalog);
}

/**
 * True if the user owns MCP on any cycle.
 * @param {object[]} subscriptions
 * @param {object|null} [catalog]
 */
export function hasActiveMcp(subscriptions, catalog = null) {
  return findActiveMcp(subscriptions, catalog) !== null;
}

/**
 * True when the user owns the flat "unlimited" Futures plan in the given subscriptions.
 * Checks the nickname on the subscription and on each line, because bundled subscriptions
 * put it on the line. Pass one cycle's subscriptions to ask about that cycle, or all of
 * them to ask "on any cycle".
 */
export function hasFuturesUnlimited(matchingSubs) {
  for (const s of matchingSubs) {
    const lineNicks = subscriptionLineItems(s).map((li) => li?.priceNickname);
    const all = [s.priceNickname, ...lineNicks].filter(Boolean);
    for (const nick of all) {
      const meta = parseNickname(nick);
      if (meta?.product === PRODUCTS.futures && meta.variant === PRICE_VARIANTS.unlimited) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Derive checkout counts/flags for a specific billing cycle.
 * Returns a partial selection patch (counts reset to 0 when no subs match).
 */
export function selectionForCycle(subscriptions, cycle, catalog = null) {
  const active = listActiveSubscriptions(subscriptions);
  const matching = active.filter((s) => subscriptionBillingCycle(s) === cycle);

  const counts = copierQuantitiesFromSubscriptions(matching, catalog);
  const hasAnalyzer = matching.some((s) => productFromSubscription(s) === ADDON_PRODUCTS.analyzer);
  const de = dedicatedEnvArrays(matching, catalog);
  const futuresUnlimited = hasFuturesUnlimited(matching);

  return {
    billingCycle: cycle,
    standardCount: counts[PRODUCTS.standard] || 0,
    premiumCount: counts[PRODUCTS.premium] || 0,
    futuresCount: counts[PRODUCTS.futures] || 0,
    futuresUnlimited,
    analyzerEnabled: hasAnalyzer,
    dedicated: de.accounts,
    dedicatedSubIds: de.subIds,
    dedicatedSubItemIds: de.subItemIds,
    dedicatedNicknames: de.nicknames,
    dedicatedSubLineCounts: de.subLineCounts,
    dedicatedKinds: de.kinds
  };
}

/** @param {string} interval @param {number} [intervalCount] */
export function intervalToBillingCycle(interval, intervalCount = 1) {
  const ic = Number(intervalCount) || 1;
  if (interval === 'year') return BILLING_CYCLES.annual;
  if (interval === 'month') {
    if (ic === 3) return BILLING_CYCLES.quarterly;
    if (ic === 6) return BILLING_CYCLES.semiAnnual;
    if (ic >= 12) return BILLING_CYCLES.annual;
    return BILLING_CYCLES.monthly;
  }
  return BILLING_CYCLES.monthly;
}

/** Infer copier product line from billing-manager subscription. */
export function productFromSubscription(sub) {
  if (!sub) return null;

  if (sub.priceNickname) {
    const meta = classifyNicknameLoose(sub.priceNickname);
    if (meta?.product) return meta.product;
  }
  return productFromDisplayName(sub.productName);
}

/**
 * Map active subscription → checkout selection (single product line).
 * @param {object} sub subscriptionToJson shape
 */
export function selectionFromActiveSubscription(sub, catalog = null) {
  if (!sub) return null;

  const lines = subscriptionLineItems(sub);
  if (lines.length > 1) {
    const counts = copierQuantitiesFromSubscriptions([sub], catalog);
    let billingCycle = intervalToBillingCycle(sub.interval, sub.intervalCount);
    const nickMeta = parseNickname(sub.priceNickname || lines[0]?.priceNickname);
    if (nickMeta?.cycle) billingCycle = nickMeta.cycle;

    return {
      ...DEFAULT_SELECTION,
      product: productFromSubscription(sub) || PRODUCTS.standard,
      billingCycle,
      standardCount: counts[PRODUCTS.standard] || 0,
      premiumCount: counts[PRODUCTS.premium] || 0,
      futuresCount: counts[PRODUCTS.futures] || 0,
      analyzerEnabled: false,
      dedicated: [],
      skipTrial: true
    };
  }

  const qty = Math.max(1, Number(sub.quantity) || lines[0]?.quantity || 1);
  let product = productFromSubscription(sub);
  let billingCycle = intervalToBillingCycle(sub.interval, sub.intervalCount);

  const nickMeta = parseNickname(sub.priceNickname || lines[0]?.priceNickname);
  if (nickMeta?.cycle) billingCycle = nickMeta.cycle;

  if (!product) product = PRODUCTS.standard;

  const next = {
    ...DEFAULT_SELECTION,
    product,
    billingCycle,
    standardCount: 0,
    premiumCount: 0,
    futuresCount: 0,
    analyzerEnabled: false,
    dedicated: [],
    skipTrial: true
  };

  if (product === PRODUCTS.standard) next.standardCount = qty;
  else if (product === PRODUCTS.premium) next.premiumCount = qty;
  else if (product === PRODUCTS.futures) {
    if (nickMeta?.variant === PRICE_VARIANTS.unlimited) {
      next.futuresUnlimited = true;
      next.futuresCount = 0;
    } else {
      next.futuresCount = qty;
    }
  }

  return next;
}

/**
 * Merge all active subscriptions into one checkout selection (multi-sub legacy accounts).
 * @param {object[]} subscriptions
 * @param {{ addOns?: string[] } | null} [billingOverview]
 * @param {object|null} [catalog]
 */
export function selectionFromAllSubscriptions(subscriptions, billingOverview = null, catalog = null) {
  // Signal Manager and EA Hosting are standalone — never let either anchor or
  // seed the trading selection. An EA-Hosting-only account has no copier plan,
  // and productFromSubscription cannot classify it, so without this filter it
  // fell through to PRODUCTS.standard and the copier checkout opened seeded as
  // if the user owned a Standard plan.
  const active = listActiveSubscriptions(subscriptions).filter((s) => !isSignalManager(s) && !isEaHosting(s));
  if (!active.length) return null;

  // Pick a primary sub to anchor the initial billing cycle, then derive counts
  // SCOPED to that cycle. Summing across all cycles would show e.g. std=6 on
  // the monthly tab when the user has 2 std on monthly + 2 on quarterly + 2 on
  // semiAnnual — confusing and wrong. The cycle tabs are mutually exclusive
  // views; counts must match the active tab.
  const primary = active[0];
  let billingCycle = intervalToBillingCycle(primary.interval, primary.intervalCount);
  const nickMeta = parseNickname(primary.priceNickname);
  if (nickMeta?.cycle) billingCycle = nickMeta.cycle;

  const cyclePatch = selectionForCycle(subscriptions, billingCycle, catalog);

  const next = {
    ...DEFAULT_SELECTION,
    ...cyclePatch,
    product: productFromSubscription(primary) || PRODUCTS.standard,
    skipTrial: true
  };

  // billingOverview's analyzer entitlement is cycle-agnostic; trust the
  // cycle-scoped analyzer flag from selectionForCycle. The drawer tab toggle
  // reflects ownership on the active cycle, not globally.
  return next;
}

/**
 * User-facing plan badge shown in the header/profile chip. Three tiers:
 *   - "Premium" when the user holds the `premium-plan` role AND owns a copier
 *     plan (Standard/Premium/Futures seats > 0).
 *   - "Standard" when the user owns a copier plan but lacks the premium-plan role
 *     (e.g. a paid Standard subscriber). Without this they wrongly showed "Free".
 *   - "Free" when no copier plan is owned (add-on-only such as Analyzer/Dedicated
 *     Environment, or nothing).
 *
 * Copier ownership is detected via `copierQuantitiesFromSubscriptions`, which is
 * line-item aware (handles bundle subs), tier-mode/legacy-nickname tolerant, and
 * falls back to the sub's top-level nickname — so it never misclassifies a copier
 * owner as an add-on.
 *
 * @param {object[]} subscriptions
 * @param {string[] | undefined} roles auth/JWT roles (e.g. user.roles)
 * @param {object | null} [catalog]
 * @returns {"Premium" | "Standard" | "Free"}
 */
export function resolvePlanBadge(subscriptions, roles, catalog = null) {
  const active = listActiveSubscriptions(subscriptions);
  const counts = copierQuantitiesFromSubscriptions(active, catalog);
  const hasCopier = (counts[PRODUCTS.standard] || 0) + (counts[PRODUCTS.premium] || 0) + (counts[PRODUCTS.futures] || 0) > 0;
  if (!hasCopier) return 'Free';
  const hasPremiumRole = Array.isArray(roles) && roles.includes('premium-plan');
  return hasPremiumRole ? 'Premium' : 'Standard';
}
